<template>
    <!-- `persistent` is kept on purpose: a stray backdrop click must not answer
         a destructive question for the user. It does mean Vuetify stops
         handling Esc for us, so Esc is wired back — but from a document-level
         listener, NOT @keydown.esc here. A handler on the dialog only fires
         when focus is already inside it, and after clicking a non-focusable
         action icon (the delete icon on Projects is a <div>) focus is still on
         <body>, so the key never reached the dialog. That is why Esc did
         nothing. See onDocumentKeydown().

         `data-esc-close="off"` stands this dialog down from the app-wide Esc
         handler (plugins/escCloseDialogs.ts). It has a condition the generic
         handler cannot know about: this dialog owes its caller a PROMISE, and
         closing it without resolving that promise leaves every
         `await this.$refs.confirm.open(...)` hanging forever. -->
    <v-dialog
        v-model="dialog"
        data-esc-close="off"
        :max-width="options.width"
        :style="{ zIndex: options.zIndex }"
        persistent
    >
        <!-- BS Labs Confirm dialog (Figma 33:625), for every caller: icon tile,
             title 16/700, message 14, Cancel (focused first) + one action. A
             destructive question gets the danger tile and button. -->
        <div class="cd-card" role="alertdialog" :aria-labelledby="`${uid}-t`" :aria-describedby="message ? `${uid}-m` : undefined">
            <span class="cd-icon" :class="`cd-icon--${tone}`" aria-hidden="true"><DsIcon :name="toneIcon" :size="20" /></span>
            <div class="cd-text">
                <h3 :id="`${uid}-t`" class="cd-title">{{ title }}</h3>
                <div v-show="!!message" :id="`${uid}-m`" class="cd-body" v-html="message"></div>
            </div>
            <div class="cd-actions">
                <button v-if="!options.noconfirm" ref="cancelBtn" type="button" class="cd-btn cd-btn--secondary" @click="cancel">
                    {{ options.cancelLabel || 'Cancel' }}
                </button>
                <button ref="okBtn" type="button" class="cd-btn" :class="`cd-btn--${tone}`" @click="agree">{{ confirmLabel }}</button>
            </div>
        </div>
    </v-dialog>
</template>
<script>
import { DsIcon } from '@/components/ds/DsIcon';

const TONE_ICONS = {
    primary: 'arrow-right',
    error: 'trash',
    warning: 'warn',
    success: 'check-circle'
};
// Callers that do not pass a tone still get the danger look when they ask to delete or remove.
const DESTRUCTIVE = /\b(delete|remove|discard)\b/i;

let seq = 0;

export default {
    components: { DsIcon },
    props: {
        /** Kept so existing `<confirm-dialog ds>` callers still compile; every dialog is the DS one now. */
        ds: { type: Boolean, default: false }
    },
    data() {
        return {
            uid: `cd-${++seq}`,
            dialog: false,
            tone: 'primary',
            resolve: null,
            reject: null,
            message: null,
            title: null,
            options: {
                color: 'grey lighten-3',
                width: 440,
                zIndex: 5000,
                noconfirm: false
            }
        };
    },

    computed: {
        toneIcon() {
            // A notice with only OK reads as information, not as a step forward.
            if (this.tone === 'primary' && this.options.noconfirm) return 'info';
            return TONE_ICONS[this.tone] || TONE_ICONS.primary;
        },
        /** The action's own verb for a delete/remove, otherwise OK (or the caller's label). */
        confirmLabel() {
            if (this.options.confirmLabel) return this.options.confirmLabel;
            if (this.tone === 'error') {
                const m = DESTRUCTIVE.exec(`${this.title || ''} ${this.message || ''}`);
                if (m) return m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase();
            }
            return 'OK';
        }
    },

    mounted() {
        // Document level, not the dialog: see the template note. Bound for the
        // component's whole life rather than toggled with `dialog`, because a
        // listener added while the dialog opens can miss a key pressed in the
        // same tick; the handler is a no-op when closed anyway.
        document.addEventListener('keydown', this.onDocumentKeydown);
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this.onDocumentKeydown);
    },

    methods: {
        open(title, message, options) {
            this.dialog = true;
            this.title = title;
            this.message = message;
            // Per question: Object.assign alone would carry one call's labels, width or
            // noconfirm (no Cancel) into the next question that did not ask for them.
            this.options = Object.assign(this.options, { confirmLabel: null, cancelLabel: null, noconfirm: false, width: 440 }, options);
            // Not kept in `options` either, for the same reason.
            const asked = options && TONE_ICONS[options.tone] && options.tone;
            const destructive = !options?.noconfirm && DESTRUCTIVE.test(`${title || ''} ${message || ''}`);
            this.tone = asked || (destructive ? 'error' : 'primary');
            // Cancel is focused first (Figma), or OK when it is the only button.
            // focusVisible: false keeps the ring for keyboard use, not on a mouse-opened dialog.
            this.$nextTick(() => setTimeout(() => (this.$refs.cancelBtn || this.$refs.okBtn)?.focus?.({ focusVisible: false }), 50));
            return new Promise((resolve, reject) => {
                this.resolve = resolve;
                this.reject = reject;
            });
        },

        onDocumentKeydown(event) {
            if (event.key !== 'Escape' || !this.dialog) return;

            // Let a nested overlay take Esc first — a v-menu or select renders
            // outside this dialog's DOM, so without this one Esc would answer
            // the confirmation when the user only meant to shut a dropdown.
            if (document.querySelector('.v-menu.v-overlay--active')) return;

            event.stopPropagation();
            this.cancel();
        },

        agree() {
            this.settle(true);
        },

        cancel() {
            this.settle(false);
        },

        /**
         * Close once and answer the caller exactly once.
         *
         * `resolve` is cleared as it is used. Esc, the Back button and a second
         * click on OK can all arrive for the same question, and calling a
         * settled promise's resolve again is a silent no-op that hides the
         * double-answer; clearing it makes the second call provably do nothing.
         */
        settle(answer) {
            const resolve = this.resolve;
            this.resolve = null;
            this.reject = null;
            this.dialog = false;
            if (resolve) resolve(answer);
        }
    }
};

// <confirm-dialog ref="confirm"></confirm-dialog>
// async buttonfunction(){
//     if(await this.$refs.confirm.open("Confirm", "Are you sure you want to delete all record?")){
//         console.log("TEST");
//     }
// },
</script>
<style scoped>
/* BS Labs Confirm dialog (Figma 33:625): 440, padding 24, gap 16, radius 12. */
.cd-card {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;
    background: var(--bs-surface);
    border: 1px solid var(--bs-border);
    border-radius: var(--bs-radius-lg);
    box-shadow:
        0 12px 16px -4px rgba(16, 24, 40, 0.08),
        0 4px 6px -2px rgba(16, 24, 40, 0.03);
    font-family: var(--bs-font);
}
.cd-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
}
.cd-icon--primary {
    background: var(--bs-primary-50);
    color: var(--bs-primary);
}
.cd-icon--error {
    background: var(--bs-danger-50);
    color: var(--bs-danger);
}
.cd-icon--warning {
    background: var(--bs-warning-50);
    color: var(--bs-warning);
}
.cd-icon--success {
    background: var(--bs-success-50);
    color: var(--bs-success);
}
.cd-text {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}
.cd-title {
    margin: 0;
    font-size: 16px;
    line-height: 24px;
    font-weight: 700;
    color: var(--bs-text);
}
.cd-body {
    font-size: var(--bs-fs-body);
    line-height: 20px;
    color: var(--bs-text-secondary);
    overflow-wrap: anywhere;
}
.cd-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
.cd-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    min-width: 72px;
    padding: 0 12px;
    border: 1px solid transparent;
    border-radius: var(--bs-radius-md);
    font-family: inherit;
    font-size: 13px;
    line-height: 20px;
    font-weight: 600;
    color: #fff;
    cursor: pointer;
    transition: background-color var(--bs-motion-fast) var(--bs-ease);
}
.cd-btn:focus-visible {
    outline: none;
    box-shadow:
        0 0 0 2px var(--bs-primary),
        0 0 0 4px var(--bs-primary-50);
}
.cd-btn--secondary {
    background: var(--bs-surface);
    border-color: var(--bs-border-strong);
    color: var(--bs-text-strong);
}
.cd-btn--secondary:hover {
    background: var(--bs-surface-subtle);
}
.cd-btn--primary {
    background: var(--bs-primary);
}
.cd-btn--primary:hover {
    background: var(--bs-primary-hover);
}
.cd-btn--error {
    background: var(--bs-danger);
}
.cd-btn--error:hover {
    background: color-mix(in srgb, var(--bs-danger) 88%, #000);
}
.cd-btn--warning {
    background: var(--bs-warning);
}
.cd-btn--success {
    background: var(--bs-success);
}
</style>
